import { useState } from "react";
import { toast } from "../lib/toast";
import { useExcelData } from "../../context/ExcelDataContext";
import { ClassificationStep } from "./steps/ClassificationStep";
import { CorrelationStep } from "./steps/CorrelationStep";
import { RepositoryStep } from "./steps/RepositoryStep";
import { AgentExtractedPartRow } from "../../context/AgentExtractedDataContext";

type WorkflowStep = "classification" | "correlation" | "repository";

const WORKFLOW_STEPS: { id: WorkflowStep; label: string }[] = [
  { id: "classification", label: "Classification" },
  { id: "correlation", label: "Correlation" },
  { id: "repository", label: "Repository" },
];

const PAGE_META: Record<WorkflowStep, { title: string; description: string }> = {
  classification: {
    title: "Classification",
    description: "Upload documents and review extracted parameters for cost model analysis.",
  },
  correlation: {
    title: "Correlation",
    description: "Cost model generation, review and comparison against supplier quote.",
  },
  repository: {
    title: "Repository",
    description: "Portfolio view of every cost-model request, document and realised saving.",
  },
};

export function CostModelWorkflowView() {
  const { navigate } = useExcelData();
  const [activeStep, setActiveStep] = useState<WorkflowStep>("classification");
  const [isInputsConfirmed, setIsInputsConfirmed] = useState(false);
  const [autoGenerateCorrelation, setAutoGenerateCorrelation] = useState(false);
  const [correlationSessionKey, setCorrelationSessionKey] = useState(0);

  const activeStepIndex = WORKFLOW_STEPS.findIndex((s) => s.id === activeStep);

  const [agentExtractedRows, setAgentExtractedRows] = useState<AgentExtractedPartRow[]>([]);

  const handleConfirmed = () => {
    setIsInputsConfirmed(true);
    setAutoGenerateCorrelation(true);
    setActiveStep("correlation");
    toast.success("Extracted parameters confirmed — proceeding to Correlation.");
  };

  const handleWorkflowReset = () => {
    setIsInputsConfirmed(false);
    setAutoGenerateCorrelation(false);
    setAgentExtractedRows([]);
    setCorrelationSessionKey((prev) => prev + 1);
  };

  return (
    <>
      <nav className="breadcrumb" aria-label="Breadcrumb">
        <span>Sourcing</span>
        <span className="breadcrumb__sep" aria-hidden="true">·</span>
        <span className="breadcrumb__current">Cost Model Workflow</span>
      </nav>

      <div className="page-header page-header--with-action">
        <h2 className="page-header__title">Cost Model Workflow</h2>
        <button type="button" className="cmw-sticky-continue__btn" onClick={() => navigate("on-demand-cost-analysis")}>
          Standalone
        </button>
        {/* <p className="page-header__description">{PAGE_META[activeStep].description}</p> */}
      </div>

      <div className="wf-stepper" aria-label="Workflow progress">
        {WORKFLOW_STEPS.map((step, index) => {
          const isLocked = index > 0 && !isInputsConfirmed;
          return (
            <>
              <button
                key={step.id}
                type="button"
                className={`wf-stepper__step${index === activeStepIndex ? " is-active" : ""}${
                  index < activeStepIndex ? " is-done" : ""
                }`}
                onClick={() => { if (!isLocked) setActiveStep(step.id); }}
                disabled={isLocked}
                aria-current={index === activeStepIndex ? "step" : undefined}
                style={isLocked ? { opacity: 0.4, cursor: "not-allowed" } : undefined}
              >
                <span className="wf-stepper__circle">{index + 1}</span>
                <span className="wf-stepper__label">{step.label}</span>
              </button>
              {index < WORKFLOW_STEPS.length - 1 && (
                <span className="wf-stepper__line" aria-hidden="true" />
              )}
            </>
          );
        })}
      </div>

      <div hidden={activeStep !== "classification"}>
        <ClassificationStep onConfirmed={handleConfirmed} 
        setAgentExtractedRows={setAgentExtractedRows}
        onWorkflowReset={handleWorkflowReset}/>
      </div>

      <div hidden={activeStep !== "correlation"}>
        <CorrelationStep
          key={correlationSessionKey}
          agentExtractedRows={agentExtractedRows}
          autoGenerateOnEnter={autoGenerateCorrelation}
          onAutoGenerateHandled={() => setAutoGenerateCorrelation(false)}
          onNavigateToRepository={() => setActiveStep("repository")}
        />
      </div>

      <div hidden={activeStep !== "repository"}>
        <RepositoryStep />
      </div>
    </>
  );
}

export default CostModelWorkflowView;
